import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * Where a task stands, as a trail: Created → Accepted → Started / Stopped → In Progress →
 * On Hold → Done → Completed. Steps behind the current one are ticked green, the current one
 * is filled in the task's own status colour, and the rest are grey.
 *
 * On Hold is a detour, not a stage every task passes: it lights up only while the task is on
 * hold (or blocked), and a task that moved past it without stopping leaves it grey.
 */
const STEPS = [
  { key: 'created', label: 'Created', icon: 'mdi:file-plus-outline', codes: ['BACKLOG', 'ASSIGNED'] },
  { key: 'accepted', label: 'Accepted', icon: 'mdi:handshake-outline', codes: ['ACCEPTED', 'NOT_STARTED'] },
  { key: 'started', label: 'Started / Stopped', icon: 'mdi:timer-play-outline', codes: ['PAUSED'] },
  { key: 'progress', label: 'In Progress', icon: 'mdi:progress-wrench', codes: ['IN_PROGRESS', 'REVISION_REQUESTED'] },
  { key: 'hold', label: 'On Hold', icon: 'mdi:pause-octagon-outline', codes: ['ON_HOLD', 'BLOCKED'] },
  { key: 'done', label: 'Done', icon: 'mdi:send-check-outline', codes: ['SUBMITTED'] },
  { key: 'completed', label: 'Completed', icon: 'mdi:check-decagram', codes: ['APPROVED', 'COMPLETED'] },
] as const;

const DONE = '#16A34A';
const IDLE = '#9CA3AF';

export default function TaskStatusTrail({
  status,
  statusLabel,
  statusColor,
}: {
  status: string;
  statusLabel: string;
  statusColor?: string | null;
}) {
  const current = STEPS.findIndex((s) => (s.codes as readonly string[]).includes(status));
  const active = statusColor || '#2563EB';
  const ended = status === 'CANCELLED' || status === 'RELEASED';

  return (
    <Box sx={{ overflowX: 'auto', py: 0.5 }}>
      <Stack direction='row' alignItems='center' sx={{ minWidth: 'max-content' }}>
        {STEPS.map((s, i) => {
          const isCurrent = i === current;
          // On Hold is ticked only while it is the current step.
          const isPast = current > i && s.key !== 'hold';
          const color = isCurrent ? active : isPast ? DONE : IDLE;
          return (
            <Stack key={s.key} direction='row' alignItems='center'>
              {i > 0 && (
                <Box sx={{ width: 28, height: 2, bgcolor: current >= i && !ended ? DONE : '#E5E7EB', mx: 0.5 }} />
              )}
              <Stack
                direction='row'
                alignItems='center'
                gap={0.75}
                sx={{
                  px: 1.25,
                  py: 0.6,
                  borderRadius: 5,
                  border: `1.5px solid ${color}`,
                  bgcolor: isCurrent ? active : isPast ? `${DONE}14` : 'transparent',
                  color: isCurrent ? '#fff' : color,
                  boxShadow: isCurrent ? `0 0 0 3px ${active}33` : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                <Icon icon={isPast ? 'mdi:check-circle' : s.icon} width={18} />
                <Typography variant='body2' fontWeight={isCurrent ? 700 : 500} sx={{ color: 'inherit' }}>
                  {s.label}
                </Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
      {(ended || current < 0) && (
        <Typography variant='caption' color={ended ? 'error.main' : 'text.secondary'} sx={{ display: 'block', mt: 1 }}>
          Current status: {statusLabel}
        </Typography>
      )}
    </Box>
  );
}
